import { useQuery, UseQueryOptions } from 'react-query';
import { fetchWrapper } from '../utils';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';

interface TenantConfigRequest {
  config?: string[];
  tenantProperty?: string[];
}

interface TenantConfigResponse {
  config?: Record<string, string>;
  tenantProperty?: Record<string, string>;
}

export interface TenantConfig {
  [key: string]: string;
}

interface UseTenantConfigOptions {
  configKeys?: string[];
  tenantPropertyKeys?: string[];
  defaultValues?: Record<string, string>;
  enabled?: boolean;
  queryOptions?: Omit<
    UseQueryOptions<TenantConfig>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
}

export const useTenantConfig = ({
  configKeys = [],
  tenantPropertyKeys = [],
  defaultValues = {},
  enabled = true,
  queryOptions = {},
}: UseTenantConfigOptions) => {
  const queryKey = [
    'ZNavTenantConfig',
    [...configKeys].sort().join(','),
    [...tenantPropertyKeys].sort().join(','),
  ];

  return useQuery<TenantConfig>(
    queryKey,
    async () => {
      const requestBody: TenantConfigRequest = {};

      if (configKeys.length > 0) {
        requestBody.config = configKeys;
      }
      if (tenantPropertyKeys.length > 0) {
        requestBody.tenantProperty = tenantPropertyKeys;
      }

      const response = await fetchWrapper<TenantConfigResponse>(
        NAVIGATION_ENDPOINTS.V1_TENANT_CONFIG,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
      );

      const tenantConfig: TenantConfig = { ...defaultValues };

      configKeys.forEach((key) => {
        if (response.config && response.config[key] !== undefined) {
          tenantConfig[key] = response.config[key];
        } else if (defaultValues[key] !== undefined) {
          tenantConfig[key] = defaultValues[key];
        } else {
          tenantConfig[key] = 'false';
        }
      });

      tenantPropertyKeys.forEach((key) => {
        if (
          response.tenantProperty &&
          response.tenantProperty[key] !== undefined
        ) {
          tenantConfig[key] = response.tenantProperty[key];
        } else if (defaultValues[key] !== undefined) {
          tenantConfig[key] = defaultValues[key];
        }
      });

      return tenantConfig;
    },
    {
      enabled:
        enabled && (configKeys.length > 0 || tenantPropertyKeys.length > 0),
      ...queryOptions,
    },
  );
};

export const useSingleTenantConfig = (
  configKey: string,
  defaultValue = 'false',
  queryOptions?: UseTenantConfigOptions['queryOptions'],
  enabled = true,
) => {
  const { data, ...rest } = useTenantConfig({
    configKeys: [configKey],
    defaultValues: { [configKey]: defaultValue },
    queryOptions,
    enabled,
  });

  return {
    value: data?.[configKey] || defaultValue,
    data,
    ...rest,
  };
};
